import { FC, ReactElement, ReactNode, useCallback } from 'react';
import { DateString, EventKeyed } from '../../contexts/EventsLazyContext';

type EventWrapper<TEventProps> = (
  props: { children: ReactNode } & TEventProps,
) => ReactElement;

export type UseSortedEventCardsParams<
  TEvent extends { id: string; date: Date } & Record<string, unknown>,
  TWrapperProps extends Record<string, unknown>,
> = {
  sortFunction: (events: TEvent[]) => TEvent[];
  EventComponent: FC<TEvent>;
  wrapperProps: (event: TEvent) => TWrapperProps;
  Wrapper?: EventWrapper<TWrapperProps>;
};

export const useSortedEventCards = <
  TEvent extends { id: string; date: Date } & Record<string, unknown>,
  TWrapperProps extends Record<string, unknown>,
>({
  sortFunction,
  EventComponent,
  wrapperProps = () => {
    return {} as TWrapperProps;
  },
  Wrapper,
}: UseSortedEventCardsParams<TEvent, TWrapperProps>) => {
  const sortAndRender = useCallback(
    (tournaments: TEvent[]) => {
      const eventsSorted = sortFunction(tournaments);
      return eventsSorted.map((event) => {
        const Card = <EventComponent {...event} />;
        return {
          key: event.id,
          Element: Wrapper ? (
            <Wrapper {...wrapperProps(event)}>{Card}</Wrapper>
          ) : (
            Card
          ),
        };
      });
    },
    [EventComponent, Wrapper, sortFunction, wrapperProps],
  );

  const sortEachDay = useCallback(
    (eventsByDay: Record<DateString, TEvent[]>) => {
      return Object.keys(eventsByDay).reduce((accumulator, dateString) => {
        const events = eventsByDay[String(dateString)];
        const EventCards = sortAndRender(events);
        accumulator[String(dateString)] = EventCards;
        return accumulator;
      }, {} as Record<DateString, EventKeyed[]>);
    },
    [sortAndRender],
  );

  return { sortEachDay };
};
